// Pure daily-budget helper. Imported by both the server (server/modules/
// rollouts/ re-exports) and the client (src/modules/rollouts/DailyBudgetCell).
// Spec location §6.1.1 nominates server/modules/rollouts/dailyBudget.ts --
// the logic lives here in src/lib/ so the client can compute the same value
// for the tooltip breakdown without crossing the module boundary into server/.
// Same precedent as src/lib/accountSort.ts (used by server/modules/accounts/).
//
// Rule set (spec §6.1.1):
//   perDay = floor(monthlyCents / 30) + floor(increaseCents / campaignDays)
//   campaignDays = floor((endMs - startMs) / 86400000) + 1
//   -- returns null (error: 'invalid_range') when endDate <= startDate
//   -- returns null (error: 'missing_dates') when either date is missing
//   -- returns null (error: 'no_budget') when monthly=0/null AND increase=0
//
// Worked example from spec conversation:
//   monthly $600 (60000c) /30  = 2000c/day
//   increase $200 (20000c) /25 = 800c/day
//   total                      = 2800c/day = $28.00/day
//
// Notes on same-day ranges: the spec's invalid-range rule is endDate <= startDate,
// which rejects same-day campaigns here. The ready-gate validator (spec §6.3)
// uses endDate >= startDate -- a row can be marked ready on a same-day campaign
// but that row's daily-budget cell renders "—" until the end date is pushed
// out. The divergence is intentional (ready state is coordination, daily
// budget is spend math) and both sides trace back to the spec.

export type DailyBudgetError = 'missing_dates' | 'invalid_range' | 'no_budget';

export type DailyBudgetResult =
  | { perDayCents: number; monthlyPerDayCents: number; increasePerDayCents: number; campaignDays: number }
  | { error: DailyBudgetError };

// Inputs accept ISO strings -- both server (post-`sendJson`) and client wire
// shapes use ISO. Budgets are integer cents.
export type DailyBudgetInput = {
  startDate: string | null;
  endDate: string | null;
  metaMonthlyBudget: number | null;
  metaCampaignIncrease: number;
  googleMonthlyBudget: number | null;
  googleCampaignIncrease: number;
};

const MS_PER_DAY = 86_400_000;

function parseCampaignDays(startDate: string | null, endDate: string | null): number | null {
  if (!startDate || !endDate) return null;
  const startMs = Date.parse(startDate);
  const endMs = Date.parse(endDate);
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return null;
  if (endMs <= startMs) return null;
  return Math.floor((endMs - startMs) / MS_PER_DAY) + 1;
}

// Inputs for isBudgetModified. Accepts a subset of AccountCampaign so tests
// and future consumers can pass partial row shapes. The *Original fields are
// optional because rows created before that snapshot landed won't carry them
// and the helper must return `false` in that case (no comparison possible =
// no warning).
export type BudgetModificationInput = {
  metaMonthlyBudget: number | null;
  metaCampaignIncrease: number;
  metaMonthlyBudgetOriginal?: number | null;
  googleMonthlyBudget: number | null;
  googleCampaignIncrease: number;
  googleMonthlyBudgetOriginal?: number | null;
};

// "Budget modified after rollout created" signal for the Daily Budget cell.
// Returns true when either:
//   - the per-row monthly has drifted from the creation-time snapshot, OR
//   - the Campaign Increase has been set to a non-zero value.
//
// Legacy rows (pre-snapshot): *Original is null/undefined -> returns false.
// Secondary rows (P2b-2 duplicate-row flow): both Monthly and Original are
// null alongside "inherits ↑" display, so null !== null short-circuits to
// false here (null === null is actually true in JS, so the guard below runs
// first). The guard on the ORIGINAL being null/undefined catches both cases.
export function isBudgetModified(
  row: BudgetModificationInput,
  platform: 'meta' | 'google',
): boolean {
  const monthly = platform === 'meta' ? row.metaMonthlyBudget : row.googleMonthlyBudget;
  const original =
    platform === 'meta' ? row.metaMonthlyBudgetOriginal : row.googleMonthlyBudgetOriginal;
  const increase = platform === 'meta' ? row.metaCampaignIncrease : row.googleCampaignIncrease;

  if (original === null || original === undefined) return false;
  if (monthly !== original) return true;
  if (increase !== 0) return true;
  return false;
}

export function computeDailyBudget(
  row: DailyBudgetInput,
  platform: 'meta' | 'google',
): DailyBudgetResult {
  const monthly = platform === 'meta' ? row.metaMonthlyBudget : row.googleMonthlyBudget;
  const increase = platform === 'meta' ? row.metaCampaignIncrease : row.googleCampaignIncrease;

  if (!row.startDate || !row.endDate) {
    return { error: 'missing_dates' };
  }
  const days = parseCampaignDays(row.startDate, row.endDate);
  if (days === null) {
    return { error: 'invalid_range' };
  }

  const monthlyCents = monthly ?? 0;
  if (monthlyCents === 0 && increase === 0) {
    return { error: 'no_budget' };
  }

  const monthlyPerDayCents = Math.floor(monthlyCents / 30);
  const increasePerDayCents = Math.floor(increase / days);
  return {
    perDayCents: monthlyPerDayCents + increasePerDayCents,
    monthlyPerDayCents,
    increasePerDayCents,
    campaignDays: days,
  };
}
